import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { PLN204_RATES, PLN204S_CODES, openingLines, type Account, type JournalSource, type OpeningColumns } from '@fsp/core';
import { api, ApiError, type Me } from '../api';
import { centsOrNull, money, moneyInput } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

type Tab = 'trial' | 'account' | 'statements' | 'annual' | 'settings';
interface TrialRow { account: string; opening: number; debit: number; credit: number; closing: number }
interface Card { opening: number; closing: number; rows: { date: string; entryId: string; source: JournalSource; sourceId: number; label: string; account: string; partner: string | null; debit: number; credit: number; balance: number }[] }
interface Section { key: string; label: string; amount: number; items: { label: string; amount: number }[] }
interface Statements { date: string; yearStart: string; pnl: { key: string; label: string; amount: number; total?: boolean }[]; balance: { assets: Section[]; liabilities: Section[]; totalAssets: number; totalLiabilities: number } }
interface Settings { startDate: string | null; lockedUntil: string | null; drift: { id: string; date: string; label: string; change: 'added' | 'removed' | 'changed' }[] }

const today = () => new Date().toLocaleDateString('sv-SE');
const yearStart = () => `${today().slice(0, 4)}-01-01`;
const lastMonthEnd = () => { const d = new Date(); d.setDate(0); return d.toLocaleDateString('sv-SE'); };
/** Debit balance positive: shown in its column. */
const sides = (v: number) => [v > 0 ? money(v) : '', v < 0 ? money(-v) : ''];
const CHANGES = { added: 'Atsirado', removed: 'Nebėra', changed: 'Pasikeitė' } as const;

/**
 * Didžioji knyga: the trial balance (apyvartų žiniaraštis), an account's card, the balance sheet and profit
 * and loss draft, and the books' start and closed period.
 */
export function BooksPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(window.location.search).get('tab') as Tab | null) ?? 'trial');
  const [from, setFrom] = useState(yearStart);
  const [to, setTo] = useState(lastMonthEnd);
  const [code, setCode] = useState('');
  const plan = useQuery({ queryKey: ['accounts'], queryFn: () => api.get<{ accounts: Account[] }>('api/v1/accounts') });
  const names = useMemo(() => new Map((plan.data?.accounts ?? []).map((a) => [a.code, a.name])), [plan.data]);
  const name = (c: string) => names.get(c) ?? '';
  const openCard = (c: string) => { setCode(c); setTab('account'); };
  const TABS: [Tab, string][] = [['trial', 'Apyvartų žiniaraštis'], ['account', 'Sąskaitos kortelė'], ['statements', 'Balansas ir pelnas'], ['annual', 'Metinės ataskaitos'], ['settings', 'Pradžia ir uždarymas']];

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Buhalterija')} title={t('Didžioji knyga')} text={t('Sąskaitų likučiai ir apyvartos, sąskaitos kortelė, balansas ir pelno (nuostolių) ataskaita.')} />}>
      <div className="books-tabs" role="tablist">
        {TABS.map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'is-active' : ''} onClick={() => setTab(id)}>{t(label)}</button>)}
      </div>
      {tab !== 'settings' && tab !== 'annual' ? (
        <div className="form-actions journal-period">
          {tab === 'statements' ? null : <label className="books-date"><span>{t('Nuo')}</span><input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>}
          <label className="books-date"><span>{tab === 'statements' ? t('Data') : t('Iki')}</span><input type="date" value={to} min={tab === 'statements' ? undefined : from} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
          {tab === 'account' ? (
            <label className="books-date books-account"><span>{t('Sąskaita')}</span>
              <select value={code} onChange={(e) => setCode(e.target.value)}>
                <option value="">{t('Pasirinkite sąskaitą')}</option>
                {(plan.data?.accounts ?? []).filter((a) => !a.hidden && a.level > 0).map((a) => <option key={a.code} value={a.code}>{' '.repeat((a.level - 1) * 2)}{a.code} {a.name}</option>)}
              </select>
            </label>
          ) : null}
          {tab === 'trial' ? <a className="button button--ghost button--small" href={`api/v1/books/trial.csv?from=${from}&to=${to}`}>CSV</a> : null}
        </div>
      ) : null}
      {tab === 'trial' ? <TrialTab from={from} to={to} name={name} onOpen={openCard} /> : null}
      {tab === 'account' ? <CardTab code={code} from={from} to={to} name={name} /> : null}
      {tab === 'statements' ? <StatementsTab date={to} /> : null}
      {tab === 'annual' ? <AnnualTab /> : null}
      {tab === 'settings' ? <SettingsTab me={me} /> : null}
    </PanelShell>
  );
}

function TrialTab({ from, to, name, onOpen }: { from: string; to: string; name: (c: string) => string; onOpen: (c: string) => void }) {
  const { t } = useI18n();
  const data = useQuery({ queryKey: ['books', 'trial', from, to], queryFn: () => api.get<{ rows: TrialRow[] }>(`api/v1/books/trial?from=${from}&to=${to}`) });
  const rows = data.data?.rows ?? [];
  const total = (f: (r: TrialRow) => number) => rows.reduce((s, r) => s + f(r), 0);
  const pos = (v: number) => Math.max(v, 0), neg = (v: number) => Math.max(-v, 0);
  return (
    <section className="panel-card">
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {rows.length ? (
        <div className="table-wrap">
          <table className="journal-lines books-trial">
            <thead>
              <tr><th rowSpan={2}>{t('Sąskaita')}</th><th rowSpan={2} /><th colSpan={2} className="center">{t('Likutis pradžioje')}</th><th colSpan={2} className="center">{t('Apyvarta')}</th><th colSpan={2} className="center">{t('Likutis pabaigoje')}</th></tr>
              <tr><th className="right">D</th><th className="right">K</th><th className="right">D</th><th className="right">K</th><th className="right">D</th><th className="right">K</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.account}>
                  <td className="account-code"><button type="button" className="text-link" onClick={() => onOpen(r.account)}>{r.account}</button></td>
                  <td>{name(r.account)}</td>
                  {sides(r.opening).map((v, i) => <td key={`o${i}`} className="right">{v}</td>)}
                  <td className="right">{r.debit ? money(r.debit) : ''}</td><td className="right">{r.credit ? money(r.credit) : ''}</td>
                  {sides(r.closing).map((v, i) => <td key={`c${i}`} className="right">{v}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot><tr>
              <td /><td>{t('Iš viso')}</td>
              <td className="right">{money(total((r) => pos(r.opening)))}</td><td className="right">{money(total((r) => neg(r.opening)))}</td>
              <td className="right">{money(total((r) => r.debit))}</td><td className="right">{money(total((r) => r.credit))}</td>
              <td className="right">{money(total((r) => pos(r.closing)))}</td><td className="right">{money(total((r) => neg(r.closing)))}</td>
            </tr></tfoot>
          </table>
        </div>
      ) : data.isSuccess ? <p className="empty">{t('Šiuo laikotarpiu įrašų nėra.')}</p> : null}
    </section>
  );
}

function CardTab({ code, from, to, name }: { code: string; from: string; to: string; name: (c: string) => string }) {
  const { t } = useI18n();
  const data = useQuery({ queryKey: ['books', 'card', code, from, to], enabled: Boolean(code), queryFn: () => api.get<Card>(`api/v1/books/account?code=${code}&from=${from}&to=${to}`) });
  const c = data.data;
  if (!code) return <section className="panel-card"><p className="empty">{t('Pasirinkite sąskaitą.')}</p></section>;
  return (
    <section className="panel-card">
      <div className="card-head"><div><p className="eyebrow">{code}</p><h2>{name(code)}</h2></div></div>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {c ? (
        <div className="table-wrap">
          <table className="journal-lines">
            <thead><tr><th>{t('Data')}</th><th>{t('Operacija')}</th><th>{t('Sąskaita')}</th><th className="right">{t('Debetas')}</th><th className="right">{t('Kreditas')}</th><th className="right">{t('Likutis')}</th></tr></thead>
            <tbody>
              <tr className="books-opening"><td>{from}</td><td>{t('Likutis pradžioje')}</td><td /><td /><td /><td className="right">{money(c.opening)}</td></tr>
              {c.rows.map((r, i) => (
                <tr key={i}>
                  <td className="journal-date">{r.date}</td>
                  <td>{r.source === 'invoice' ? <Link to="/journal">{r.label}</Link> : r.label}{r.partner ? <small>{r.partner}</small> : null}</td>
                  <td className="account-code">{r.account}</td>
                  <td className="right">{r.debit ? money(r.debit) : ''}</td><td className="right">{r.credit ? money(r.credit) : ''}</td>
                  <td className="right">{money(r.balance)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td>{to}</td><td>{t('Likutis pabaigoje')}</td><td />
              <td className="right">{money(c.rows.reduce((s, r) => s + r.debit, 0))}</td><td className="right">{money(c.rows.reduce((s, r) => s + r.credit, 0))}</td>
              <td className="right">{money(c.closing)}</td></tr></tfoot>
          </table>
        </div>
      ) : null}
      <p className="muted account-note">{t('Likutis: debetas minus kreditas.')}</p>
    </section>
  );
}

function StatementsTab({ date }: { date: string }) {
  const { t } = useI18n();
  const data = useQuery({ queryKey: ['books', 'statements', date], queryFn: () => api.get<Statements>(`api/v1/books/statements?date=${date}`) });
  const s = data.data;
  const side = (sections: Section[], total: number, title: string) => (
    <div className="books-side">
      <h3>{t(title)}</h3>
      {sections.map((x) => (
        <div key={x.key} className="books-section">
          <p className="books-row books-row--head"><span>{x.key}. {t(x.label)}</span><span>{money(x.amount)}</span></p>
          {x.items.filter((i) => i.amount).map((i) => <p key={i.label} className="books-row"><span>{t(i.label)}</span><span>{money(i.amount)}</span></p>)}
        </div>
      ))}
      <p className="books-row books-row--total"><span>{t('Iš viso')}</span><span>{money(total)}</span></p>
    </div>
  );
  return <>
    {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
    {s ? <>
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{s.date}</p><h2>{t('Balansas')}</h2></div></div>
        <div className="books-balance">
          {side(s.balance.assets, s.balance.totalAssets, 'Turtas')}
          {side(s.balance.liabilities, s.balance.totalLiabilities, 'Nuosavas kapitalas ir įsipareigojimai')}
        </div>
        {s.balance.totalAssets !== s.balance.totalLiabilities ? <Alert kind="error">{t('Balansas nesutampa: {sum}', { sum: money(s.balance.totalAssets - s.balance.totalLiabilities) })}</Alert> : null}
      </section>
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{s.yearStart} – {s.date}</p><h2>{t('Pelno (nuostolių) ataskaita')}</h2></div></div>
        <div className="books-side">
          {s.pnl.map((p) => <p key={p.key} className={`books-row${p.total ? ' books-row--head' : ''}`}><span>{t(p.label)}</span><span>{money(p.amount)}</span></p>)}
        </div>
      </section>
      <p className="muted account-note">{t('Tai juodraštis pagal sąskaitų grupes: prieš teikiant Registrų centrui buhalteris palygina jį su oficialia forma.')}</p>
    </> : null}
  </>;
}

function SettingsTab({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const readOnly = me.company.role === 'viewer' || Boolean(me.viewAsAdmin);
  const data = useQuery({ queryKey: ['books', 'settings'], queryFn: () => api.get<Settings>('api/v1/books/settings') });
  const [start, setStart] = useState<string | null>(null);
  const [until, setUntil] = useState(lastMonthEnd);
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['books'] }).then(() => queryClient.invalidateQueries({ queryKey: ['journal'] })) });
  const s = data.data;
  if (!s) return data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null;
  return <>
    {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
    <section className="panel-card">
      <div className="card-head"><div><p className="eyebrow">{t('Pradžia')}</p><h2>{t('Apskaitos pradžia')}</h2></div></div>
      <p className="muted">{t('Nuo šios dienos dokumentai registruojami žurnale. Ankstesni dokumentai neįtraukiami — jų rezultatą pradžios dieną įveskite Žurnale rankiniu įrašu „Įžanginiai likučiai“ (pinigai, skolos, kapitalas, nepaskirstytasis pelnas).')}</p>
      <div className="form-actions">
        <input type="date" aria-label={t('Apskaitos pradžia')} value={start ?? s.startDate ?? ''} disabled={readOnly || Boolean(s.lockedUntil)} onChange={(e) => setStart(e.target.value)} className="books-input" />
        {readOnly || s.lockedUntil ? null : <button type="button" className="button button--primary button--small" disabled={act.isPending || start === null} onClick={() => act.mutate(() => api.put('api/v1/books/settings', { startDate: start || null }))}>{t('Išsaugoti')}</button>}
        {s.startDate ? <Link to="/journal" className="text-link">{t('Įvesti įžanginius likučius')}</Link> : null}
      </div>
      {s.lockedUntil ? <p className="muted account-note">{t('Kol laikotarpis uždarytas, pradžios keisti negalima.')}</p> : null}
    </section>

    {readOnly ? null : <OpeningCard startDate={s.startDate} locked={Boolean(s.lockedUntil && s.startDate && s.startDate <= s.lockedUntil)} />}

    <section className="panel-card">
      <div className="card-head"><div><p className="eyebrow">{t('Uždarymas')}</p><h2>{s.lockedUntil ? t('Uždaryta iki {date}', { date: s.lockedUntil }) : t('Laikotarpis neuždarytas')}</h2></div></div>
      <p className="muted">{t('Uždarius laikotarpį jo įrašai išsaugomi tokie, kokie yra: vėliau pakeitus dokumentą knygos nesikeičia, o pakeitimas parodomas čia. Uždaryti galima, kai nebelieka neįtrauktų operacijų.')}</p>
      {readOnly ? null : (
        <div className="form-actions">
          <input type="date" aria-label={t('Uždaryti iki')} value={until} onChange={(e) => setUntil(e.target.value)} className="books-input" />
          <button type="button" className="button button--primary button--small" disabled={act.isPending} onClick={() => act.mutate(() => api.post('api/v1/books/lock', { until }))}>{t('Uždaryti iki šios dienos')}</button>
          {s.lockedUntil ? <button type="button" className="button button--ghost button--small" disabled={act.isPending} onClick={() => { if (window.confirm(t('Atidaryti visą uždarytą laikotarpį? Įrašai vėl bus skaičiuojami iš dabartinių dokumentų.'))) act.mutate(() => api.post('api/v1/books/unlock', { until: null })); }}>{t('Atidaryti')}</button> : null}
        </div>
      )}
      {s.drift.length ? <>
        <Alert kind="error">{t('Po uždarymo pakeisti dokumentai ({n}): uždarytose knygose jie lieka tokie, kokie buvo.', { n: s.drift.length })}</Alert>
        <table className="journal-list"><tbody>
          {s.drift.map((d) => <tr key={d.id}><td className="journal-date">{d.date}</td><td>{d.label}</td><td>{t(CHANGES[d.change])}</td></tr>)}
        </tbody></table>
      </> : null}
    </section>
  </>;
}

/**
 * Įžanginiai likučiai from the previous program's trial balance (.xlsx or .csv): the columns as guessed, which
 * the person can change; accounts the chart lacks are added as sub-accounts when the account above exists,
 * otherwise mapped to one of ours.
 */
function OpeningCard({ startDate, locked }: { startDate: string | null; locked: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const plan = useQuery({ queryKey: ['accounts'], queryFn: () => api.get<{ accounts: Account[] }>('api/v1/accounts') });
  const current = useQuery({ queryKey: ['books', 'opening'], queryFn: () => api.get<{ entryId: number | null }>('api/v1/books/opening') });
  type SaftInfo = { company: { name: string; code: string }; periodEnd: string; parties: { name: string; companyCode: string | null; vatCode: string | null; address: string | null; customer: boolean }[] };
  const [file, setFile] = useState<{ rows: string[][]; columns: OpeningColumns; saft?: SaftInfo } | null>(null);
  const [saftDone, setSaftDone] = useState<string | null>(null);
  const [map, setMap] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const accounts = useMemo(() => new Map((plan.data?.accounts ?? []).map((a) => [a.code, a])), [plan.data]);
  const postable = (plan.data?.accounts ?? []).filter((a) => a.postable && !a.hidden);

  const read = async (f: File) => {
    setError(null); setDone(null); setSaftDone(null); setMap({}); setBusy(true);
    const body = new FormData();
    body.append('file', f);
    try {
      const r = await fetch('api/v1/books/opening/read', { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
      const data = await r.json().catch(() => null) as ({ rows: string[][]; columns: OpeningColumns; saft?: SaftInfo } & { error?: { code?: string } }) | null;
      if (!r.ok || !data?.rows) throw new ApiError(r.status, data?.error?.code ?? 'server_error');
      setFile(data);
    } catch (e) { setError(e); setFile(null); } finally { setBusy(false); }
  };

  const lines = file ? openingLines(file.rows, file.columns) : [];
  // Where each line goes: ours as is, a new sub-account under an existing one, or the one chosen.
  const target = (code: string) => {
    if (map[code]) return { code: map[code]!, how: 'mapped' as const };
    const own = accounts.get(code);
    if (own) return own.postable && !own.hidden ? { code, how: 'ok' as const } : { code: '', how: 'group' as const };
    const parent = [...accounts.keys()].filter((c) => c.length >= 2 && code.startsWith(c)).sort((a, b) => b.length - a.length)[0];
    return parent ? { code, how: 'new' as const } : { code: '', how: 'missing' as const };
  };
  const rows = lines.map((l) => ({ ...l, to: target(l.account) }));
  const unmapped = rows.filter((r) => !r.to.code).length;
  const debit = rows.reduce((s, r) => s + Math.max(r.balance, 0), 0), credit = rows.reduce((s, r) => s + Math.max(-r.balance, 0), 0);
  const columnSelect = (value: number | null, onChange: (v: number | null) => void, label: string, empty?: string) => (
    <label className="books-date"><span>{label}</span>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}>
        {empty ? <option value="">{empty}</option> : null}
        {(file?.rows[file.columns.header] ?? []).map((h, i) => <option key={i} value={i}>{String.fromCharCode(65 + i)}{h ? ` · ${h.slice(0, 30)}` : ''}</option>)}
      </select>
    </label>
  );
  const setColumns = (patch: Partial<OpeningColumns>) => file && setFile({ ...file, columns: { ...file.columns, ...patch } });
  const customersOf = (s: SaftInfo) => s.parties.filter((p) => p.customer);
  const importCustomers = async (s: SaftInfo) => {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ added: number; skipped: number }>('api/v1/customers/import', { customers: customersOf(s).map((p) => ({ name: p.name.slice(0, 200), companyCode: p.companyCode ?? '', vatCode: p.vatCode ?? '', address: p.address ?? '' })) });
      setSaftDone(t('Klientai perkelti: {added} nauji, {skipped} jau buvo.', { added: r.added, skipped: r.skipped }));
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const save = async () => {
    const replace = Boolean(current.data?.entryId);
    if (replace && !window.confirm(t('Įžanginiai likučiai jau įvesti. Pakeisti juos šiais?'))) return;
    setBusy(true); setError(null);
    try {
      const merged = new Map<string, { account: string; name: string; balance: number }>();
      for (const r of rows) {
        const m = merged.get(r.to.code) ?? { account: r.to.code, name: r.to.how === 'new' ? r.name : '', balance: 0 };
        m.balance += r.balance;
        merged.set(r.to.code, m);
      }
      const result = await api.post<{ added: string[] }>('api/v1/books/opening', { lines: [...merged.values()], replace });
      setDone(result.added.length ? t('Įžanginiai likučiai įvesti. Sukurtos subsąskaitos: {list}.', { list: result.added.join(', ') }) : t('Įžanginiai likučiai įvesti.'));
      setFile(null);
      await Promise.all(['books', 'journal', 'accounts'].map((k) => queryClient.invalidateQueries({ queryKey: [k] })));
    } catch (e) { setError(e); } finally { setBusy(false); }
  };

  return (
    <section className="panel-card">
      <div className="card-head"><div><p className="eyebrow">{t('Įžanginiai likučiai')}</p><h2>{t('Likučiai iš ankstesnės programos')}</h2></div></div>
      <p className="muted">{t('Įkelkite ankstesnės programos (pvz., Rivilės) apyvartų žiniaraštį – Excel (.xlsx) arba CSV failą – arba jos SAF-T (XML) rinkmeną. Imami likučiai pabaigoje; jie įrašomi apskaitos pradžios dieną vienu įrašu.')}</p>
      {!startDate ? <p className="account-problem">{t('Pirmiausia nurodykite apskaitos pradžią.')}</p> : locked ? <p className="account-problem">{t('Apskaitos pradžia yra uždarytame laikotarpyje.')}</p> : <>
        {current.data?.entryId ? <p className="muted">{t('Įžanginiai likučiai jau įvesti ({date}). Įkėlus naują failą jie bus pakeisti.', { date: startDate })} <Link to="/journal">{t('Žurnalas')}</Link></p> : null}
        <input type="file" accept=".xlsx,.csv,.txt,.xml" disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = ''; }} />
        {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
        {done ? <Alert kind="success">{done}</Alert> : null}
        {saftDone ? <Alert kind="success">{saftDone}</Alert> : null}
        {file?.saft ? <div className="declaration-row">
          <div><strong>SAF-T · {file.saft.company.name} {file.saft.company.code}</strong><small>{t('Likučiai {date} dienos pabaigai', { date: file.saft.periodEnd })}</small></div>
          {customersOf(file.saft).length ? <button type="button" className="button button--ghost button--small" disabled={busy} onClick={() => void importCustomers(file.saft!)}>{t('Perkelti klientus ({n})', { n: customersOf(file.saft).length })}</button> : null}
        </div> : null}
        {file ? <>
          <div className="form-actions journal-period">
            {columnSelect(file.columns.account, (v) => setColumns({ account: v ?? 0 }), t('Sąskaita'))}
            {columnSelect(file.columns.balance !== null ? null : file.columns.debit, (v) => setColumns({ debit: v, balance: null }), t('Likutis D'), '—')}
            {columnSelect(file.columns.balance !== null ? null : file.columns.credit, (v) => setColumns({ credit: v, balance: null }), t('Likutis K'), '—')}
            {columnSelect(file.columns.balance, (v) => setColumns({ balance: v }), t('Arba vienas likučio stulpelis (D +, K −)'), '—')}
          </div>
          <div className="table-wrap">
            <table className="journal-lines">
              <thead><tr><th>{t('Sąskaita faile')}</th><th>{t('Pavadinimas')}</th><th className="right">D</th><th className="right">K</th><th>{t('Mūsų sąskaita')}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.account}>
                    <td className="account-code">{r.account}</td>
                    <td>{r.name}</td>
                    <td className="right">{r.balance > 0 ? money(r.balance) : ''}</td><td className="right">{r.balance < 0 ? money(-r.balance) : ''}</td>
                    <td>
                      {r.to.how === 'ok' ? <span>{r.account} {accounts.get(r.account)?.name}</span>
                        : r.to.how === 'new' ? <span>{t('Bus sukurta subsąskaita')}</span>
                        : <select value={map[r.account] ?? ''} onChange={(e) => setMap({ ...map, [r.account]: e.target.value })} className={map[r.account] ? '' : 'is-missing'}>
                            <option value="">{r.to.how === 'group' ? t('Sąskaita turi subsąskaitų – pasirinkite') : t('Nėra plane – pasirinkite')}</option>
                            {postable.filter((a) => a.code[0] === r.account[0]).map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                          </select>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td /><td>{t('Iš viso')}</td><td className="right">{money(debit)}</td><td className="right">{money(credit)}</td>
                <td>{debit !== credit ? <span className="account-problem">{t('Skirtumas {sum}', { sum: money(debit - credit) })}</span> : null}</td></tr></tfoot>
            </table>
          </div>
          <div className="form-actions">
            <button type="button" className="button button--primary" disabled={busy || !rows.length || unmapped > 0 || debit !== credit} onClick={() => void save()}>{t('Įrašyti įžanginius likučius')}</button>
            <button type="button" className="button button--ghost" onClick={() => setFile(null)}>{t('Atšaukti')}</button>
            {unmapped ? <span className="account-problem">{t('Nepasirinkta sąskaitų: {n}', { n: unmapped })}</span> : null}
          </div>
        </> : null}
      </>}
    </section>
  );
}

interface AnnualRow { code: string; label: string; current: number; previous: number; total?: boolean }

/**
 * Metinės finansinės ataskaitos on the official forms, to enter in Registrų centras' self-service (it takes no
 * file): a very small company's short forms or a small company's abridged balance sheet and full profit and loss.
 */
function AnnualTab() {
  const { t } = useI18n();
  const [year, setYear] = useState(() => new Date().getFullYear() - 1);
  const [size, setSize] = useState<'very_small' | 'small' | 'mb'>('very_small');
  const data = useQuery({ queryKey: ['books', 'annual', year], queryFn: () => api.get<{ forms: Record<string, AnnualRow[]> }>(`api/v1/books/annual?year=${year}`) });
  const forms = size === 'very_small' ? [['short_balance', 'Trumpas balansas'], ['short_pnl', 'Trumpa pelno (nuostolių) ataskaita']] : size === 'mb' ? [['mb_balance', 'Balansas (38 VAS)'], ['mb_pnl', 'Pelno (nuostolių) ataskaita (38 VAS)']] : [['abridged_balance', 'Sutrumpintas balansas'], ['pnl', 'Pelno (nuostolių) ataskaita']];
  const nf = (v: number) => (v ? v.toLocaleString('lt-LT') : '–');
  return <>
    <div className="form-actions journal-period no-print">
      <label className="books-date"><span>{t('Metai')}</span>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>{[0, 1, 2, 3].map((n) => { const y = new Date().getFullYear() - n; return <option key={y} value={y}>{y}</option>; })}</select></label>
      <label className="books-date"><span>{t('Įmonė')}</span>
        <select value={size} onChange={(e) => setSize(e.target.value as 'very_small' | 'small' | 'mb')}>
          <option value="very_small">{t('Labai maža (trumpos formos)')}</option>
          <option value="small">{t('Maža (sutrumpintas balansas, pelno (nuostolių) ataskaita)')}</option>
          <option value="mb">{t('Mažoji bendrija (38 VAS)')}</option>
        </select></label>
      <button type="button" className="button button--ghost button--small" onClick={() => window.print()}>{t('Spausdinti / PDF')}</button>
    </div>
    {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
    {data.data ? forms.map(([key, title]) => {
      const rows = data.data!.forms[key!] ?? [];
      const assets = rows.find((r) => r.label === 'TURTO IŠ VISO'), equity = rows.find((r) => r.label === 'NUOSAVO KAPITALO IR ĮSIPAREIGOJIMŲ IŠ VISO');
      const off = assets && equity && (assets.current !== equity.current || assets.previous !== equity.previous);
      return (
        <section className="panel-card annual-form" key={key}>
          <div className="card-head"><div><p className="eyebrow">{year}-12-31 · {t('eurais')}</p><h2>{t(title!)}</h2></div>
            <a className="button button--ghost button--small no-print" href={`api/v1/books/annual.csv?year=${year}&form=${key}`}>CSV</a></div>
          <table className="journal-lines">
            <thead><tr><th /><th>{t('Straipsnis')}</th><th className="right">{year} m.</th><th className="right">{year - 1} m.</th></tr></thead>
            <tbody>{rows.map((r, i) => (
              <tr key={i} className={r.total || /^[A-H]$/.test(r.code) ? 'annual-total' : ''}><td className="account-code">{r.code}</td><td>{t(r.label)}</td><td className="right">{nf(r.current)}</td><td className="right">{nf(r.previous)}</td></tr>
            ))}</tbody>
          </table>
          {size === 'mb' && key === 'mb_pnl' ? <p className="muted account-note">{t('Mažajai bendrijai būtinas ir laisvos formos aiškinamasis raštas (38 VAS 144 p.): teisinė forma, pavadinimas, kodas, buveinė, registravimo data, veiklos aprašymas, vidutinis darbuotojų skaičius, kad taikomas 38 VAS, apskaitos politikos pakeitimai, narių įnašai, avansu išmokėtas pelnas, suteiktos garantijos.')}</p> : null}
          {off ? <Alert kind="error">{t('Dėl apvalinimo turtas ir nuosavas kapitalas su įsipareigojimais skiriasi – buhalteris pakoreguoja vieną eilutę.')}</Alert> : null}
        </section>
      );
    }) : null}
    <Pln204Card key={year} year={year} />
    <p className="muted account-note no-print">{t('Registrų centras failų nepriima: šias sumas buhalteris įveda savitarnoje („Finansinių ataskaitų rinkinių teikimas“, tikslumas – 1 Eur), o patvirtintas ataskaitas prideda PDF formatu. Mažosios bendrijos gali naudoti 38 VAS formas.')}</p>
  </>;
}

type YesNo = 'T' | 'N';
const PLN204_QUESTIONS: [keyof Pln204Answers, string][] = [
  ['e9', '9. Ar buvo vykdomi sandoriai su vienetais tikslinėse teritorijose?'],
  ['e10', '10. Ar vienetas buvo pertvarkytas, reorganizuotas ar dalyvavo reorganizavime ar perleidime (PMĮ 41 str.)?'],
  ['fr0528', '17. Teikiama FR0528 (sandoriai su asocijuotais asmenimis)?'],
  ['fr0438', '17. Teikiama FR0438?'],
  ['e17a', '17A. Ar palūkanų sąnaudoms taikomas PMĮ 30-1 str. 7 d. 1 p.?'],
  ['e17c', '17C. Ar pildoma IV dalis (kredito įstaigoms)?'],
];
interface Pln204Answers { e9: YesNo; e10: YesNo; fr0528: YesNo; fr0438: YesNo; e17a: YesNo; e17c: YesNo }

/**
 * PLN204 draft: the year's figures from the books; the accountant answers the form's questions, lists the
 * non-deductible expenses by code and the tax losses carried forward, picks the rate; the FFData file for EDS.
 */
function Pln204Card({ year }: { year: number }) {
  const { t } = useI18n();
  const data = useQuery({ queryKey: ['books', 'pln204', year], queryFn: () => api.get<{ income: number; profitBeforeTax: number; fines: number; employees: number; suggestion: { rateCode: 1 | 2 | 3; nonDeductible: { code: string; amount: number }[] } }>(`api/v1/books/pln204?year=${year}`) });
  const [answers, setAnswers] = useState<Pln204Answers>({ e9: 'N', e10: 'N', fr0528: 'N', fr0438: 'N', e17a: 'N', e17c: 'N' });
  const [rateCode, setRateCode] = useState<1 | 2 | 3 | null>(null);
  const [nd, setNd] = useState<{ code: string; amount: string }[] | null>(null);
  const [loss, setLoss] = useState('');
  const [error, setError] = useState<unknown>(null);
  const d = data.data;
  const rows = nd ?? (d?.suggestion.nonDeductible ?? []).map((n) => ({ code: n.code, amount: moneyInput(n.amount) }));
  const body = d ? { year, answers, rateCode: rateCode ?? d.suggestion.rateCode, lossCarried: Math.max(0, Math.round(Number(loss.replace(/\s/g, '').replace(',', '.')) || 0)),
    nonDeductible: rows.filter((r) => r.code && (centsOrNull(r.amount) ?? 0) > 0).map((r) => ({ code: r.code, amount: centsOrNull(r.amount)! })) } : null;
  const preview = useQuery({ queryKey: ['books', 'pln204', 'preview', body], enabled: Boolean(body), queryFn: () => api.post<{ lines: { box: string; amount: number }[] }>('api/v1/books/pln204/preview', body) });
  const download = async () => {
    setError(null);
    try {
      const r = await fetch('api/v1/books/pln204.ffdata', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'x-fsp-request': '1' }, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(() => null) as { error?: { code?: string } } | null; throw new ApiError(r.status, e?.error?.code ?? 'server_error'); }
      const href = URL.createObjectURL(await r.blob());
      const a = document.createElement('a'); a.href = href; a.download = `PLN204_${year}.ffdata`; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (e) { setError(e); }
  };
  const LABELS: Record<string, string> = { '4': 'Vidutinis darbuotojų skaičius', '18': 'Į mokesčio bazę įtrauktų pajamų suma', '19': 'Pelnas (nuostoliai) prieš apmokestinimą', '20': 'Neleidžiami atskaitymai (PLN204S)',
    '37': 'Mokestinio laikotarpio veiklos pelnas (nuostoliai)', '40': 'Pelnas', '41': 'Nuostoliai', '45': 'Atskaityti ankstesnių metų nuostoliai', '48': 'Apmokestinamasis pelnas', '54A': 'Tarifas, %', '55': 'Pelno mokesčio suma', '64': 'Mokėtina (−) suma' };
  return (
    <section className="panel-card no-print pln204">
      <div className="card-head"><div><p className="eyebrow">PLN204 · {year}</p><h2>{t('Pelno mokesčio deklaracija (juodraštis)')}</h2></div>
        <button type="button" className="button button--primary button--small" disabled={!body} onClick={() => void download()}>{t('PLN204 failas (EDS)')}</button></div>
      <p className="muted">{t('Pajamos ir pelnas – iš didžiosios knygos. Buhalteris atsako į klausimus, nurodo neleidžiamus atskaitymus ir perkeliamus nuostolius. Dividendai, VP, užsienio mokesčiai ir kiti priedai neužpildomi.')}</p>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      {d ? <div className="pln204-grid">
        <div>
          {PLN204_QUESTIONS.map(([k, q]) => (
            <label key={k} className="pln204-q"><span>{t(q)}</span>
              <select value={answers[k]} onChange={(e) => setAnswers({ ...answers, [k]: e.target.value as YesNo })}><option value="N">{t('Ne')}</option><option value="T">{t('Taip')}</option></select></label>
          ))}
          <label className="pln204-q"><span>{t('Pelno mokesčio tarifas')}</span>
            <select value={rateCode ?? d.suggestion.rateCode} onChange={(e) => setRateCode(Number(e.target.value) as 1 | 2 | 3)}>
              {PLN204_RATES.map((r) => <option key={r.code} value={r.code}>{r.code} – {t(r.label)} ({r.rate(year)} %)</option>)}
            </select></label>
          <label className="pln204-q"><span>{t('Ankstesnių metų mokestiniai nuostoliai, atskaitomi šiais metais (Eur)')}</span><input inputMode="numeric" value={loss} onChange={(e) => setLoss(e.target.value)} placeholder="0" /></label>
          <p className="eyebrow">{t('Neleidžiami atskaitymai (PLN204S)')}</p>
          {rows.map((r, i) => (
            <div key={i} className="pln204-nd">
              <select value={r.code} onChange={(e) => setNd(rows.map((x, k) => (k === i ? { ...x, code: e.target.value } : x)))}>
                <option value="">—</option>{PLN204S_CODES.map((c) => <option key={c.code} value={c.code}>{c.code} {t(c.label)}</option>)}</select>
              <input inputMode="decimal" value={r.amount} onChange={(e) => setNd(rows.map((x, k) => (k === i ? { ...x, amount: e.target.value } : x)))} aria-label={t('Suma')} />
              <button type="button" className="icon-button" aria-label={t('Pašalinti')} onClick={() => setNd(rows.filter((_, k) => k !== i))}>×</button>
            </div>
          ))}
          <button type="button" className="text-link" onClick={() => setNd([...rows, { code: '', amount: '' }])}>+ {t('Eilutė')}</button>
        </div>
        <table className="journal-lines">
          <tbody>{(preview.data?.lines ?? []).map((l) => (
            <tr key={l.box} className={l.box === '64' || l.box === '55' ? 'annual-total' : ''}><td className="account-code">{l.box}</td><td>{t(LABELS[l.box] ?? l.box)}</td><td className="right">{l.amount.toLocaleString('lt-LT')}</td></tr>
          ))}</tbody>
        </table>
      </div> : null}
    </section>
  );
}
